import type { CssGeneratorContext } from '../css-generator.model';

/**
 * `app-editable-row-table`: a wrapper that scrolls a table wider than its container sideways, a fixed-layout table
 * whose heading cells size the columns, the column headers, the body cells, a divider under each row and the stacked
 * `.cell-label` each cell shows below `lg` in the header's text styling. Stacking and hiding stay on the template.
 */
export const styles = ({ dt }: CssGeneratorContext) => `
app-editable-row-table {
  display: block;
}

.app-editable-row-table {
  overflow-x: ${dt('appEditableRowTable.wrapper.overflowX')};
  overflow-y: ${dt('appEditableRowTable.wrapper.overflowY')};
  scrollbar-gutter: ${dt('appEditableRowTable.wrapper.scrollbarGutter')};
  width: 100%;

  & table {
    width: 100%;
    min-width: ${dt('appEditableRowTable.table.minWidth')};
    table-layout: fixed;
    border-collapse: collapse;
    border: none;
  }

  & thead th {
    background: ${dt('appEditableRowTable.th.background')};
    color: ${dt('appEditableRowTable.th.color')};
    font-size: ${dt('appEditableRowTable.th.fontSize')};
    font-weight: ${dt('appEditableRowTable.th.fontWeight')};
    line-height: ${dt('appEditableRowTable.th.lineHeight')};
    padding: ${dt('appEditableRowTable.th.padding')};
    text-align: left;
    vertical-align: top;
    border: 0;
    border-bottom: ${dt('appEditableRowTable.th.borderBottomWidth')} solid ${dt('appEditableRowTable.th.borderBottomColor')};
  }

  & tbody tr {
    background: ${dt('appEditableRowTable.row.background')};
    border-bottom: 1px solid ${dt('appEditableRowTable.row.borderColor')};
  }

  & tbody td {
    padding: ${dt('appEditableRowTable.td.padding')};
    font-size: ${dt('appEditableRowTable.td.fontSize')};
    vertical-align: ${dt('appEditableRowTable.td.verticalAlign')};
  }

  & tbody td .cell-label {
    color: ${dt('appEditableRowTable.th.color')};
    font-size: ${dt('appEditableRowTable.th.fontSize')};
    font-weight: ${dt('appEditableRowTable.th.fontWeight')};
    line-height: ${dt('appEditableRowTable.th.lineHeight')};
  }

  /*
   * Optimus DatePicker + p-motion (Optimus v1): the overlay is appended to the body while open, but the panel moves
   * back into the host on leave, which inside the scrolling wrapper briefly widens the scroll overflow and flickers
   * the scrollbar:
   * - the panel's min-width applies only while the panel is inside the host (not while appended to the body);
   * - p-motion stays a zero-width flex item, so the calendar is not laid out as a third flex column for a frame
   *   before its inline position:absolute applies (onOverlayBeforeEnter);
   * - contain:layout on the host limits outward width contributions from internal layout passes;
   * - overflow-x:clip with overflow-y:hidden keeps the host from widening the scroll width; the row height is the
   *   input's only.
   */
  & tbody td .p-datepicker {
    contain: layout;
    overflow-x: clip;
    overflow-y: hidden;
  }

  & tbody td .p-datepicker .p-datepicker-panel {
    min-width: unset;
  }

  & tbody td .p-datepicker p-motion {
    flex: 0 0 0;
    width: 0;
    min-width: 0;
    overflow: visible;
    align-self: flex-start;
    position: relative;
  }
}
`;
